import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckSquare, Download, Plus, RefreshCw, Tag, Users, X } from 'lucide-react';
import { toast } from 'sonner@2.0.3';

import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useAccountStore, getAccountStatus, platformLabel, tiktokToAccount, type Account } from '../../entities/account';
import { ConnectAccountDialog, type ConnectPlatform } from '../../features/account-add';
import { AccountsListSkeleton } from '../../widgets/accounts-list';
import { AccountCardInteractive, PlatformAccountCard } from '../../widgets/account-card-interactive';
import { BulkTagsDialog } from '../../widgets/account-tags-dialog';
import {
  AccountsFilterBar, computeAccountCounts, filterAccounts, parseAccountsFilter, writeAccountsFilter, type AccountsFilter,
} from '../../features/accounts-status-filter';
import { useLoadAccounts } from '../../features/accounts-load';
import { getTikTokAccounts, getTikTokStats, invalidateTikTokAccounts } from '../../shared/api/tiktok.api';
import { youtubeApi, type YouTubeChannelRecord } from '../../shared/api/youtube.api';
import { accountsApi } from '../../shared/api/accounts.api';
import { TopbarActions } from '../../shared/ui/TopbarSlot';

const AVATARS_CACHE_KEY = 'avatars_last_refresh';
/** Сколько подсвечивается только что добавленная карточка. */
const HIGHLIGHT_MS = 4000;

function youtubeToAccount(c: YouTubeChannelRecord): Account {
  return {
    id: c.id,
    platform: 'youtube',
    name: c.name,
    username: c.handle ? c.handle.replace(/^@/, '') : undefined,
    profilePicture: c.thumbnail ?? undefined,
    // Канал по ключу API: токена нет, протухать нечему.
    isActive: true,
    createdAt: new Date(0),
    updatedAt: new Date(0),
    tags: [],
  };
}

function youtubeUrl(c: YouTubeChannelRecord): string | undefined {
  if (c.link) return c.link;
  if (c.handle) return `https://www.youtube.com/${c.handle.startsWith('@') ? c.handle : `@${c.handle}`}`;
  if (c.channelId) return `https://www.youtube.com/channel/${c.channelId}`;
  return undefined;
}

function profileUrl(a: Account, youtube: Map<string, YouTubeChannelRecord>): string | undefined {
  if (a.platform === 'youtube') {
    const c = youtube.get(a.id);
    return c ? youtubeUrl(c) : undefined;
  }
  if (!a.username) return undefined;
  return a.platform === 'tiktok' ? `https://www.tiktok.com/@${a.username}` : `https://www.instagram.com/${a.username}/`;
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Все подключённые аккаунты одной сеткой: Instagram (KV), TikTok (Display API),
 * YouTube (каналы по ключу API). Threads сюда пока не попадает: у бэкенда нет
 * списка Threads-аккаунтов для сотрудников (только у партнёра — свои).
 */
export function AccountsPage() {
  const { isAdmin } = useCurrentUser();
  const igAccounts = useAccountStore((state) => state.accounts);
  const isAccountsLoading = useAccountStore((state) => state.isLoading);
  const { loadAccounts } = useLoadAccounts();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = useMemo(() => parseAccountsFilter(searchParams), [searchParams]);
  const setFilter = (next: AccountsFilter) => {
    setSearchParams((prev) => writeAccountsFilter(prev, next), { replace: true });
  };

  // ── TikTok: список + подписчики отдельным запросом на каждый аккаунт ──
  const [ttAccounts, setTtAccounts] = useState<Account[]>([]);
  const [ttFollowers, setTtFollowers] = useState<Record<string, number | null>>({});
  const [ttLoaded, setTtLoaded] = useState(false);
  const ttRequested = useRef(new Set<string>());
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const loadTikTok = useCallback(async (): Promise<Account[]> => {
    let list: Awaited<ReturnType<typeof getTikTokAccounts>> = [];
    try {
      list = await getTikTokAccounts();
    } catch {
      // TikTok не настроен / бэк недоступен — страница остаётся без него.
    }
    const accounts = list.map(tiktokToAccount);
    if (!alive.current) return accounts;
    setTtAccounts(accounts);
    setTtLoaded(true);
    // Подписчиков дотягиваем в фоне и только у тех, кого ещё не спрашивали.
    void (async () => {
      for (const a of list) {
        if (ttRequested.current.has(a.openId)) continue;
        ttRequested.current.add(a.openId);
        let followers: number | null = null;
        try {
          followers = (await getTikTokStats(a.openId)).followerCount;
        } catch {
          // Нет цифры — покажем прочерк.
        }
        if (!alive.current) return;
        setTtFollowers((prev) => ({ ...prev, [a.openId]: followers }));
      }
    })();
    return accounts;
  }, []);

  // ── YouTube: только каналы со ссылкой; записи «Монтажа» одним именем — не аккаунты ──
  const [ytChannels, setYtChannels] = useState<YouTubeChannelRecord[]>([]);
  const [ytLoaded, setYtLoaded] = useState(false);
  const loadYouTube = useCallback(async (): Promise<Account[]> => {
    let channels: YouTubeChannelRecord[] = [];
    try {
      channels = (await youtubeApi.channels()).channels.filter((c) => (c.channelId || c.link) && !c.partnerUserId);
    } catch {
      // Нет ключа YouTube на бэкенде — без каналов.
    }
    if (alive.current) {
      setYtChannels(channels);
      setYtLoaded(true);
    }
    return channels.map(youtubeToAccount);
  }, []);

  const ytById = useMemo(() => new Map(ytChannels.map((c) => [c.id, c])), [ytChannels]);
  const ytAccounts = useMemo(() => ytChannels.map(youtubeToAccount), [ytChannels]);
  const ttWithFollowers = useMemo(
    () => ttAccounts.map((a) => {
      const f = a.openId ? ttFollowers[a.openId] : undefined;
      return f == null ? a : { ...a, followersCount: f };
    }),
    [ttAccounts, ttFollowers],
  );

  const all = useMemo(() => [...igAccounts, ...ttWithFollowers, ...ytAccounts], [igAccounts, ttWithFollowers, ytAccounts]);
  const filtered = useMemo(() => filterAccounts(all, filter), [all, filter]);
  const counts = useMemo(() => computeAccountCounts(all, filter), [all, filter]);
  const allTags = useMemo(
    () => [...new Set(igAccounts.flatMap((a) => a.tags ?? []))].sort((a, b) => a.localeCompare(b, 'ru')),
    [igAccounts],
  );

  // ── Только что добавленные: подсветить, когда появятся в списке ──
  const knownIds = useRef<Set<string> | null>(null);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Перевыпуск протухших ссылок на аватарки — в DataProvider; здесь только списки.
    void Promise.all([loadAccounts(), loadTikTok(), loadYouTube()]).then(([, tt, yt]) => {
      knownIds.current = new Set([...useAccountStore.getState().accounts, ...tt, ...yt].map((a) => a.id));
    });
  }, [loadAccounts, loadTikTok, loadYouTube]);

  const [connectOpen, setConnectOpen] = useState(false);

  // Раньше новый TikTok появлялся только после перезагрузки страницы:
  // теперь после любого входа перечитываем все списки.
  const handleConnected = async (_platform: ConnectPlatform, _outcome: 'connected' | 'closed') => {
    const before = knownIds.current;
    const [, tt, yt] = await Promise.all([loadAccounts(), loadTikTok(), loadYouTube()]);
    const ids = [...useAccountStore.getState().accounts, ...tt, ...yt].map((a) => a.id);
    knownIds.current = new Set(ids);
    if (!before || !alive.current) return;
    const fresh = ids.filter((id) => !before.has(id));
    if (fresh.length === 0) return;
    setHighlighted(new Set(fresh));
    setTimeout(() => { if (alive.current) setHighlighted(new Set()); }, HIGHLIGHT_MS);
  };

  // ── Выбор для массовых тэгов (тэги есть только у Instagram) ──
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState<'add' | 'remove' | null>(null);
  const selectableFiltered = filtered.filter((a) => (a.platform ?? 'instagram') === 'instagram');
  const selectedAccounts = igAccounts.filter((a) => selected.has(a.id));

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };

  const exportToCSV = () => {
    // Что видно на экране — то и в файле; внутренних id там нет (02.11.2026).
    const headers = ['Платформа', 'Username', 'Название', 'Статус', 'Подписчики', 'Тэги'];
    const rows = filtered.map((account) => [
      platformLabel(account.platform ?? 'instagram'),
      account.username ? `@${account.username}` : '',
      account.name || '',
      getAccountStatus(account) === 'active' ? 'Активен' : 'Не активен',
      account.followersCount != null ? String(account.followersCount) : '',
      (account.tags ?? []).join(', '),
    ]);

    const csvContent = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `accounts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`CSV: ${rows.length} аккаунтов`);
  };

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      await accountsApi.refreshAll();
      try { localStorage.setItem(AVATARS_CACHE_KEY, Date.now().toString()); } catch { /* нет хранилища */ }
      // TikTok: свежий список и заново подписчики.
      invalidateTikTokAccounts();
      ttRequested.current.clear();
      await Promise.all([loadAccounts(), loadTikTok(), loadYouTube()]);
      toast.success('Данные всех аккаунтов обновлены!');
    } catch (error) {
      console.error('Error refreshing accounts:', error);
      toast.error('Ошибка при обновлении данных');
    } finally {
      setIsRefreshing(false);
    }
  };

  const isFiltered = filter.platform !== 'all' || filter.status !== 'all' || filter.query.trim() !== '' || filter.tags.length > 0;
  const everythingLoaded = !isAccountsLoading && ttLoaded && ytLoaded;

  return (
    <div className="space-y-6">
      {isAdmin && (
        <TopbarActions>
          <Button onClick={() => setConnectOpen(true)}>
            <Plus />
            Добавить аккаунт
          </Button>
        </TopbarActions>
      )}
      <ConnectAccountDialog open={connectOpen} onOpenChange={setConnectOpen} audience="staff" onConnected={handleConnected} />

      {all.length === 0 && !everythingLoaded ? (
        <>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-9 w-[420px] max-w-full rounded-xl" />
          <AccountsListSkeleton />
        </>
      ) : all.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Нет подключенных аккаунтов</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {isAdmin
                ? 'Подключите первый аккаунт: Instagram, TikTok, YouTube или Threads'
                : 'Аккаунты подключает администратор'}
            </p>
            {isAdmin && (
              <Button onClick={() => setConnectOpen(true)}>
                <Plus />
                Добавить аккаунт
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Заголовок раздела живёт в AccountsLayout — здесь только счётчик и служебные действия */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {isFiltered ? `Показано ${filtered.length} из ${all.length}` : `Всего аккаунтов: ${all.length}`}
            </p>
            <div className="flex flex-wrap gap-2">
              {igAccounts.length > 0 && !selecting && (
                <Button variant="outline" size="sm" onClick={() => setSelecting(true)}>
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Выбрать
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshData}
                  disabled={isRefreshing}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Обновить все
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={exportToCSV} disabled={filtered.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Экспорт в CSV
              </Button>
            </div>
          </div>

          <AccountsFilterBar value={filter} onChange={setFilter} counts={counts} tags={allTags} />

          {selecting && (
            <div className="sticky top-16 z-10 flex flex-wrap items-center gap-2 rounded-lg border bg-background/95 px-3 py-2 shadow-sm backdrop-blur">
              <span className="text-sm font-medium">Выбрано: {selected.size}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected(new Set(selectableFiltered.map((a) => a.id)))}
                disabled={selectableFiltered.length === 0}
              >
                Выбрать все{isFiltered ? ' по фильтру' : ''} ({selectableFiltered.length})
              </Button>
              {selected.size > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Снять</Button>
              )}
              <div className="ml-auto flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setBulkMode('add')} disabled={selected.size === 0}>
                  <Tag />
                  Добавить тэг
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBulkMode('remove')} disabled={selected.size === 0}>
                  Убрать тэг
                </Button>
                <Button size="sm" variant="ghost" onClick={stopSelecting} aria-label="Закончить выбор">
                  <X />
                  Готово
                </Button>
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center gap-3 p-12">
                <p className="text-sm text-muted-foreground text-center">Под фильтр не подошёл ни один аккаунт</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilter({ platform: 'all', status: 'all', query: '', tags: [] })}
                >
                  Сбросить фильтр
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((account) => {
                const platform = account.platform ?? 'instagram';
                if (platform === 'instagram') {
                  return (
                    <AccountCardInteractive
                      key={account.id}
                      account={account}
                      highlighted={highlighted.has(account.id)}
                      selection={selecting ? { selected: selected.has(account.id), onToggle: () => toggleSelected(account.id) } : undefined}
                    />
                  );
                }
                return (
                  // Тэгов у TikTok и YouTube нет — в режиме выбора их не выбрать.
                  <div key={account.id} className={selecting ? 'pointer-events-none opacity-50' : undefined}>
                    <PlatformAccountCard
                      account={account}
                      profileUrl={profileUrl(account, ytById)}
                      followersLoading={platform === 'tiktok' && !!account.openId && !(account.openId in ttFollowers)}
                      highlighted={highlighted.has(account.id)}
                      onChanged={() => void handleConnected('tiktok', 'closed')}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <BulkTagsDialog
        mode={bulkMode}
        accounts={selectedAccounts}
        knownTags={allTags}
        onOpenChange={(open) => { if (!open) setBulkMode(null); }}
      />
    </div>
  );
}
